/**
 * ===========================================================================
 *  SQUADS — the background war
 * ===========================================================================
 *
 * A Dynasty Warriors battlefield is not a bag of individuals: it is thirty
 * small units, each with a standard, a shape and somewhere to be. The player
 * is one strong piece inside a battle that would happen without him, and that
 * is most of what makes a wide shot read as a war instead of a mob.
 *
 * A squad owns:
 *   - an **anchor** (a virtual point that marches, so the formation survives
 *     the death of its leader),
 *   - a **facing**,
 *   - a **formation** (line at a gate, column on a road, wedge on a charge),
 *   - an **order** (hold / advance / escort / engage / rout).
 *
 * Members only ever read `slotPos()`; they never talk to each other. Squad
 * bookkeeping is O(squads) per think, not O(units).
 *
 * Zero allocation after `init()`: the registry is a fixed set of typed arrays
 * with a free list, exactly like `Entities`.
 */
import { ST, TEAM } from '../core/entities.js';
import { ORDERS, FORM, hash32 } from './store.js';
import { ROADS, RIVER, BRIDGE, HAS_BRIDGE } from '../world/layout.js';
import { WALLS, route as wallRoute, pushOut, clear as wallClear, ROUTE_CAP } from '../world/walls.js';

/** `wp` value for a march through the arena's gates (1 and 2 are the bridge). */
export const WP_WALLS = 3;
const _wa = { x: 0, z: 0, nx: 0, nz: 0 };

/**
 * `Math.hypot` is NOT a cheap intrinsic in V8: it is a variadic builtin that
 * boxes its arguments, and it measured at ~155 bytes of garbage per call
 * against ~0 for `Math.sqrt`. This module calls it once or more per unit per
 * fixed step, which is the whole of the AI's allocation rate and therefore the
 * whole of `aiCpuPeak` — the mean was never the problem, the scavenges landing
 * inside the unit loop were. Same arithmetic, no allocation.
 */
const hyp = (x, z) => Math.sqrt(x * x + z * z);


const _r = { d: 0, s: 0, cx: 0, cz: 0, tx: 1, tz: 0 };
const _r2 = { d: 0, s: 0, cx: 0, cz: 0, tx: 1, tz: 0 };
const _rv = { d: 0, s: 0, cx: 0, cz: 0, tx: 1, tz: 0 };
const _at = { x: 0, z: 0, tx: 0, tz: 0 };
export const _slot = { x: 0, z: 0 };

/**
 * Which bank of the Brantas a point is on. The river is a polyline, so "east"
 * "west" is the sign of the cross product with the local tangent — that stays
 * correct where the channel bends back on itself near the northern gorge.
 */
export function riverSide(x, z) {
  RIVER.query(x, z, _rv);
  return ((x - _rv.cx) * _rv.tz - (z - _rv.cz) * _rv.tx) >= 0 ? 1 : -1;
}
export function riverDist(x, z) { RIVER.query(x, z, _rv); return _rv.d; }
/** The bank each bridge abutment sits on, resolved once.
 *  This runs at IMPORT, so on an arena with no bridge it was a crash on boot,
 *  not a misbehaviour in a fight. route() already returns early when nothing
 *  is within 150 m of the river, so with no river no squad ever takes the
 *  bridge branch and the placeholder is never read. */
const SIDE_A = HAS_BRIDGE ? riverSide(BRIDGE.ax, BRIDGE.az) : 1;

export class Squads {
  constructor(cap = 128) {
    this.cap = cap;
    this.alive = new Uint8Array(cap);
    this.team = new Int8Array(cap);
    this.form = new Int8Array(cap);
    this.order = new Int8Array(cap);
    this.prevOrder = new Int8Array(cap);
    this.ax = new Float32Array(cap); this.az = new Float32Array(cap);
    this.fx = new Float32Array(cap); this.fz = new Float32Array(cap);
    this.gx = new Float32Array(cap); this.gz = new Float32Array(cap);
    this.speed = new Float32Array(cap);
    this.spacing = new Float32Array(cap);
    this.count = new Int16Array(cap);
    this.size = new Int16Array(cap);
    this.next = new Int16Array(cap);
    this.leader = new Int32Array(cap);
    this.banner = new Int32Array(cap);      // the standard bearer, or -1
    this.baseId = new Int16Array(cap);
    this.escort = new Int32Array(cap);
    this.timer = new Float32Array(cap);
    this.quietT = new Float32Array(cap);
    this.morale = new Float32Array(cap);
    this.road = new Int8Array(cap);
    /** Re-tasking bookkeeping — see `src/ai/command.js`. */
    this.since = new Float32Array(cap);     // seconds spent with nothing to do
    this.obj = new Int8Array(cap);          // OBJ.* — what it was last sent to do
    this.objSq = new Int16Array(cap);       // the enemy squad it was sent at
    this.routT = new Float32Array(cap);     // seconds of running left
    this.wp = new Int8Array(cap);           // bridge waypoint stage (0 = direct)
    this.wpx = new Float32Array(cap); this.wpz = new Float32Array(cap);
    /** A march through walls (wp = WP_WALLS): the gate waypoints from
     *  `walls.route()`, `wpN` of them, `wpK` the one being walked to. */
    this.wpPath = new Float32Array(cap * ROUTE_CAP * 2);
    this.wpN = new Int8Array(cap); this.wpK = new Int8Array(cap);
    /** Where this squad last saw an enemy. Its men close on THIS, not on their
     *  formation slot — a squad that is engaged but whose rear rank cannot see
     *  the enemy from where it stands is a squad standing still in a fight. */
    this.foeX = new Float32Array(cap); this.foeZ = new Float32Array(cap);
    this.foeT = new Float32Array(cap);      // how stale that sighting is
    /** Where the men ACTUALLY are, refreshed by `ai.js trackSquadMen()`.
     *  The anchor is a virtual point and nothing used to read back the body it
     *  is supposed to be the origin of, so the loop was open: measured over a
     *  40 s battle, an ENGAGE squad's men sat a mean 9.3-15.4 m off their slots
     *  with the anchor 8.3-14.4 m behind them (49 m at worst), because ENGAGE
     *  freezes the anchor while the men close on `foeX/foeZ`. A squad was
     *  hunting its next foe with a 22 m radius centred a squad's length behind
     *  itself. `cx,cz` is the living centroid; `sex,sez` is the mean slot error
     *  (the average man's offset from the slot he was given), which is the
     *  correction the anchor needs and which is zero when the formation is
     *  where it should be. `tracked` is the member count behind them. */
    this.cx = new Float32Array(cap); this.cz = new Float32Array(cap);
    this.sex = new Float32Array(cap); this.sez = new Float32Array(cap);
    this.tracked = new Int16Array(cap);
    this.peak = new Int16Array(cap);        // biggest this squad has ever been
    /* DEV_PLAN Step 5. Seconds for which a PLAYER order outranks the command
     * layer. src/ai/command.js re-tasks a HOLD squad within a second of it
     * settling, so without this an order from the player would be overwritten
     * before its squad had taken three paces — the order layer would obey the
     * letter of `order[i]` and ignore the player completely. */
    this.playerT = new Float32Array(cap);
    // Inactive references use -1 both at construction and after retry. A zero
    // here is a live entity/squad slot, not an empty sentinel.
    this.leader.fill(-1); this.banner.fill(-1); this.baseId.fill(-1);
    this.escort.fill(-1); this.objSq.fill(-1);
    this.n = 0;
    this._free = new Int32Array(cap);
    this._freeN = cap;
    for (let i = 0; i < cap; i++) this._free[i] = cap - 1 - i;
  }

  /** Restore the registry to its construction state for a fresh battle. */
  resetBattle() {
    this.alive.fill(0); this.team.fill(0); this.form.fill(0); this.order.fill(0); this.prevOrder.fill(0);
    this.ax.fill(0); this.az.fill(0); this.fx.fill(0); this.fz.fill(0);
    this.gx.fill(0); this.gz.fill(0); this.speed.fill(0); this.spacing.fill(0);
    this.count.fill(0); this.size.fill(0); this.next.fill(0);
    this.leader.fill(-1); this.banner.fill(-1); this.baseId.fill(-1); this.escort.fill(-1);
    this.timer.fill(0); this.quietT.fill(0); this.morale.fill(0); this.road.fill(0);
    this.since.fill(0); this.obj.fill(0); this.objSq.fill(-1); this.routT.fill(0);
    this.wp.fill(0); this.wpx.fill(0); this.wpz.fill(0);
    this.wpPath.fill(0); this.wpN.fill(0); this.wpK.fill(0);
    this.playerT.fill(0);
    this.foeX.fill(0); this.foeZ.fill(0); this.foeT.fill(0); this.peak.fill(0);
    this.cx.fill(0); this.cz.fill(0); this.sex.fill(0); this.sez.fill(0); this.tracked.fill(0);
    this.n = 0;
    this._freeN = this.cap;
    for (let i = 0; i < this.cap; i++) this._free[i] = this.cap - 1 - i;
    return this;
  }

  create(team, x, z, o = null) {
    if (this._freeN === 0) return -1;
    const i = this._free[--this._freeN];
    this.alive[i] = 1;
    this.team[i] = team;
    this.form[i] = o?.form ?? FORM.LINE;
    this.order[i] = o?.order ?? ORDERS.HOLD;
    this.prevOrder[i] = this.order[i];
    this.ax[i] = x; this.az[i] = z;
    this.cx[i] = x; this.cz[i] = z;               // until the first track pass
    this.sex[i] = 0; this.sez[i] = 0; this.tracked[i] = 0;
    this.gx[i] = o?.gx ?? x; this.gz[i] = o?.gz ?? z;
    const fx = o?.fx ?? 0, fz = o?.fz ?? 1;
    const fl = hyp(fx, fz) || 1;
    this.fx[i] = fx / fl; this.fz[i] = fz / fl;
    this.speed[i] = o?.speed ?? 2.1;
    this.spacing[i] = o?.spacing ?? 1.85;
    this.count[i] = 0; this.size[i] = 0; this.next[i] = 0;
    this.leader[i] = -1; this.banner[i] = -1; this.baseId[i] = o?.baseId ?? -1;
    this.escort[i] = o?.escort ?? -1;
    this.timer[i] = 0.05 + (i % 11) * 0.031;      // stagger squad thinking
    this.quietT[i] = 0; this.morale[i] = 0.68 + (i % 7) * 0.02;
    this.road[i] = o?.road ? 1 : 0;
    this.since[i] = 0; this.obj[i] = 0; this.objSq[i] = -1; this.routT[i] = 0;
    this.playerT[i] = 0;          // a recycled slot inherits no standing order
    this.wp[i] = 0; this.peak[i] = 0; this.wpN[i] = 0; this.wpK[i] = 0;
    this.n++;
    // A squad is born with its goal (a wave, a knot sent at the fight) and
    // nothing used to route it: on open ground the only obstacle was the
    // river and those squads were seeded on the right bank. Inside walls a
    // squad born in the wrong court would march at a wall for the whole
    // battle. Walls only, so Lembah Brantas's births are exactly as before.
    if (WALLS.n) this.route(i);
    return i;
  }

  disband(i) {
    if (!this.alive[i]) return;
    this.alive[i] = 0; this.n--;
    this._free[this._freeN++] = i;
  }

  /** Hand out the next formation slot. */
  join(i) { const s = this.next[i]++; this.count[i]++; this.size[i] = Math.max(this.size[i], s + 1); return s; }
  leave(i) { if (i >= 0 && this.alive[i]) { this.count[i]--; if (this.count[i] <= 0) this.disband(i); } }

  setOrder(i, order, gx, gz) {
    if (!this.alive[i]) return;
    this.prevOrder[i] = this.order[i];
    this.order[i] = order;
    if (gx !== undefined) { this.gx[i] = gx; this.gz[i] = gz; }
    this.since[i] = 0;
    this.route(i);
  }

  /**
   * Plan the way to `gx,gz`. The only real obstacle on this map is the river —
   * a body that walks at its goal in a straight line either swims or stands on
   * the bank forever, which is exactly what the two knots at the crossing did.
   * If the goal is on the far bank, the march is broken into two legs through
   * the bridge abutments.
   */
  route(i) {
    this.wp[i] = 0;
    if (WALLS.n) { this._wallRoute(i); return; }
    const ax = this.ax[i], az = this.az[i], gx = this.gx[i], gz = this.gz[i];
    // nothing within reach of the river can need the bridge
    if (riverDist(ax, az) > 150 && riverDist(gx, gz) > 150) return;
    const mine = riverSide(ax, az);
    if (mine === riverSide(gx, gz)) return;
    const nearA = mine === SIDE_A;
    this.wpx[i] = nearA ? BRIDGE.ax : BRIDGE.bx;
    this.wpz[i] = nearA ? BRIDGE.az : BRIDGE.bz;
    this.wp[i] = 1;
  }

  /** Plan the gates between the anchor and the goal (walled arenas). */
  _wallRoute(i) {
    const K = ROUTE_CAP, off = i * K * 2;
    const n = wallRoute(this.ax[i], this.az[i], this.gx[i], this.gz[i], this.wpPath, off, K);
    this.wpN[i] = n; this.wpK[i] = 0;
    if (n > 0) {
      this.wpx[i] = this.wpPath[off]; this.wpz[i] = this.wpPath[off + 1];
      this.wp[i] = WP_WALLS;
    } else this.wp[i] = 0;
  }

  /** The point the anchor is actually walking to right now. */
  goalOf(i, out) {
    if (this.wp[i] > 0) { out.x = this.wpx[i]; out.z = this.wpz[i]; }
    else { out.x = this.gx[i]; out.z = this.gz[i]; }
    return out;
  }

  /** Called when the anchor reaches its current waypoint. Returns true if done. */
  advanceWaypoint(i) {
    if (this.wp[i] === WP_WALLS) {
      const k = ++this.wpK[i];
      if (k < this.wpN[i]) {
        const off = (i * ROUTE_CAP + k) * 2;
        this.wpx[i] = this.wpPath[off]; this.wpz[i] = this.wpPath[off + 1];
        return false;
      }
      // Path walked. Plan again from here: a no-op when the goal is in sight,
      // and the recovery if the path was cut short or the goal has moved.
      this._wallRoute(i);
      return this.wp[i] === 0;
    }
    if (this.wp[i] === 1) {                       // at the near abutment: cross
      const far = riverSide(this.wpx[i], this.wpz[i]) === SIDE_A;
      this.wpx[i] = far ? BRIDGE.bx : BRIDGE.ax;
      this.wpz[i] = far ? BRIDGE.bz : BRIDGE.az;
      this.wp[i] = 2;
      return false;
    }
    this.wp[i] = 0;
    return true;
  }

  /** World position of formation slot `s`. Writes into `_slot`. */
  slotPos(i, s) {
    const sp = this.spacing[i];
    let ox = 0, oz = 0;
    switch (this.form[i]) {
      case FORM.COLUMN: {
        const file = s & 1, rank = s >> 1;
        ox = (file - 0.5) * sp * 1.15;
        oz = -rank * sp * 1.05;
        break;
      }
      case FORM.WEDGE: {
        const r = Math.floor((Math.sqrt(8 * s + 1) - 1) / 2);
        const k = s - (r * (r + 1)) / 2;
        ox = (k - r * 0.5) * sp * 1.1;
        oz = -r * sp * 0.95;
        break;
      }
      case FORM.BLOB: {
        const h = (s * 2654435761) >>> 0;
        ox = (((h & 255) / 255) - 0.5) * sp * 4.2;
        oz = ((((h >>> 8) & 255) / 255) - 0.5) * sp * 4.2;
        break;
      }
      default: {                                   // LINE — ranks of eight
        const col = s % 8, row = (s / 8) | 0;
        ox = (col - 3.5) * sp;
        oz = -row * sp * 1.25;
        break;
      }
    }
    // Even spacing at rest is its own tell: ten men on a road shoulder at
    // exactly 1.9 m read as a slot grid, not as a unit standing about. A
    // deterministic per-(squad, slot) offset makes the rank ragged without
    // costing anything or breaking reproducibility.
    const h = hash32(((i + 1) * 2654435761) ^ ((s + 1) * 40503));
    ox += ((h & 1023) / 1023 - 0.5) * sp * 0.46;
    oz += (((h >>> 10) & 1023) / 1023 - 0.5) * sp * 0.46;
    const fx = this.fx[i], fz = this.fz[i];
    _slot.x = this.ax[i] + fz * ox + fx * oz;
    _slot.z = this.az[i] - fx * ox + fz * oz;
    return _slot;
  }

  /**
   * Per-frame anchor motion + per-think re-planning.
   *
   * Three things happen here that did not before:
   *
   *  - an ADVANCE that arrives does not just stop, it **asks for the next
   *    job** (`since = 99`), which `src/ai/command.js` answers on its next
   *    slice. A squad is never out of work for more than a fraction of a
   *    second, so the war away from the player keeps moving;
   *  - a march whose goal is on the far bank of the Brantas is walked in two legs
   *    through the bridge abutments instead of straight at the water;
   *  - a squad that has been gutted breaks and runs (ORDERS.ROUT) and then
   *    rallies, which is the only way a morale system is ever visible.
   */
  update(w, dt) {
    const e = w.entities;
    for (let i = 0; i < this.cap; i++) {
      if (!this.alive[i]) continue;
      if (this.count[i] <= 0) { this.disband(i); continue; }
      if (this.count[i] > this.peak[i]) this.peak[i] = this.count[i];

      const ord = this.order[i];
      this.since[i] += dt;
      this.foeT[i] += dt;
      if (this.routT[i] > 0) this.routT[i] -= dt;

      // ---------------------------------------------------------- anchor
      if (ord === ORDERS.ADVANCE || ord === ORDERS.ESCORT || ord === ORDERS.ROUT) {
        // Which leg are we walking? An escorted squad follows its officer; a
        // crossing squad walks to its abutment; everyone else to the goal.
        let legX = this.gx[i], legZ = this.gz[i], leg = 0;
        if (ord === ORDERS.ESCORT) {
          const id = this.escort[i];
          if (id >= 0 && e.alive[id] && e.state[id] !== ST.DEAD) { legX = e.x[id]; legZ = e.z[id]; }
        } else if (this.wp[i] > 0) { legX = this.wpx[i]; legZ = this.wpz[i]; leg = 1; }

        // roads only on a direct leg — the bridge waypoints are more specific
        let mx = legX, mz = legZ;
        if (this.road[i] && leg === 0 && ord === ORDERS.ADVANCE) {
          this._road(this.ax[i], this.az[i], legX, legZ);
          mx = _at.x; mz = _at.z;
        }

        const dx = mx - this.ax[i], dz = mz - this.az[i];
        const d = hyp(dx, dz);
        if (d > 0.30) {
          const v = Math.min(this.speed[i], d * 1.6);
          this.ax[i] += (dx / d) * v * dt;
          this.az[i] += (dz / d) * v * dt;
          // the formation always faces the leg it is walking
          const k = 1 - Math.exp(-2.4 * dt);
          let nfx = this.fx[i] + (dx / d - this.fx[i]) * k;
          let nfz = this.fz[i] + (dz / d - this.fz[i]) * k;
          const nl = hyp(nfx, nfz) || 1;
          this.fx[i] = nfx / nl; this.fz[i] = nfz / nl;
        }

        const legD = hyp(legX - this.ax[i], legZ - this.az[i]);
        if (this.wp[i] === WP_WALLS && ord === ORDERS.ESCORT) {
          // An escort's anchor rides ON its officer, so anchor-to-leg is the
          // distance to him and would tick the gates off as fast as they were
          // read. He walks the gates (officer.js), so his distance decides.
          const id = this.escort[i];
          const ox = id >= 0 && e.alive[id] ? e.x[id] : this.ax[i];
          const oz = id >= 0 && e.alive[id] ? e.z[id] : this.az[i];
          if (hyp(this.wpx[i] - ox, this.wpz[i] - oz) < 4.0) this.advanceWaypoint(i);
        } else if (this.wp[i] > 0) {
          // the abutments are wide; 4 m is "on the ramp"
          if (legD < 4.0) this.advanceWaypoint(i);
        } else if (ord === ORDERS.ADVANCE && legD < 1.6) {
          this.order[i] = ORDERS.HOLD;
          this.obj[i] = 0; this.objSq[i] = -1;
          this.since[i] = 99;                    // -> re-tasked on the next slice
        }
      }

      // ------------------------------------------- the anchor follows its men
      // ADVANCE/ESCORT/ROUT drive the anchor kinematically above and it keeps
      // up (measured: 4.8-5.3 m from the centroid on the march, slot error
      // 1.4-1.9 m). HOLD and ENGAGE do not drive it at all, and ENGAGE is where
      // the men leave: they close on `foeX/foeZ`, not on their slots, so the
      // body of the squad walks out from under its own origin and the foe
      // search on the next think runs from empty ground behind the fight.
      //
      // The correction is the mean slot error, not the centroid. Setting the
      // anchor to the centroid would run away -- a LINE hangs its ranks BEHIND
      // the anchor, so the mean slot offset is non-zero and the formation would
      // walk itself forward by half its own depth every pass. The mean slot
      // ERROR is zero exactly when every man is where he was told to be, so
      // this settles instead of drifting, and it needs no formation algebra.
      if (this.tracked[i] > 0 && (ord === ORDERS.ENGAGE || ord === ORDERS.HOLD)) {
        const ex = this.sex[i], ez = this.sez[i];
        const el = hyp(ex, ez);
        if (el > 0.35) {                           // ignore formation jitter
          // Rate-limited: the origin walks after the men at squad pace, it is
          // not teleported onto them, so the formation stays a formation and a
          // single man who has run off cannot drag the anchor with him.
          const v = Math.min(this.speed[i], el * 1.2);
          this.ax[i] += (ex / el) * v * dt;
          this.az[i] += (ez / el) * v * dt;
        }
      }

      // An anchor is a virtual point and nothing else would stop it. A routed
      // march never brings it near masonry; this is for the moves that are
      // not routed — an escort cutting a corner after its officer, a rout's
      // first step, the hold correction dragging it after men at a wall.
      if (WALLS.n && pushOut(this.ax[i], this.az[i], 1.2, _wa)) { this.ax[i] = _wa.x; this.az[i] = _wa.z; }

      // ------------------------------------------------------- re-plan
      this.timer[i] -= dt;
      if (this.timer[i] > 0) continue;
      this.timer[i] = 0.28 + ((i * 7) % 13) * 0.014;

      // ---- routed: run, then rally ------------------------------------
      if (ord === ORDERS.ROUT) {
        if (this.routT[i] <= 0) {
          this.morale[i] = 0.52;
          this.order[i] = ORDERS.HOLD;
          this.wp[i] = 0; this.obj[i] = 0; this.objSq[i] = -1;
          this.since[i] = 99;
          this._reanchor(i, e);
        }
        continue;
      }

      const foeTeam = this.team[i] === TEAM.PLAYER ? TEAM.ENEMY : TEAM.PLAYER;
      // Look from where the men are. The anchor lags them by 8-14 m in ENGAGE
      // even with the correction above (it walks, it does not snap), and 22 m
      // of vision centred a squad's length to the rear is how an engaged squad
      // loses the fight it is standing in and drops back to HOLD on `quietT`.
      const px = this.tracked[i] > 0 ? this.cx[i] : this.ax[i];
      const pz = this.tracked[i] > 0 ? this.cz[i] : this.az[i];
      let foe = e.nearest(px, pz, 22, foeTeam, -1);
      // a foe on the far side of a wall is not a fight this squad can join
      if (foe >= 0 && WALLS.n && WALLS.blocked(px, pz, e.x[foe], e.z[foe], 0)) foe = -1;

      // ---- has this squad been broken? --------------------------------
      // Squad morale is only ever spent by `ai.js onDeath` (a member -0.062, a
      // captain -0.19, the standard bearer -0.30) and only ever recovered in
      // HOLD, so this fires when a squad has lost roughly half of itself or had
      // its banner put on the ground. Half a squad down is a rout in any
      // Warriors game.
      if (this.morale[i] < 0.22 && this.count[i] > 0) {
        let fx = this.fx[i], fz = this.fz[i];
        if (foe >= 0) {
          fx = this.ax[i] - e.x[foe]; fz = this.az[i] - e.z[foe];
          const fl = hyp(fx, fz) || 1; fx /= fl; fz /= fl;
        } else { fx = -fx; fz = -fz; }
        this.prevOrder[i] = ORDERS.HOLD;
        this.order[i] = ORDERS.ROUT;
        this.gx[i] = this.ax[i] + fx * 62; this.gz[i] = this.az[i] + fz * 62;
        this.speed[i] = 4.4;
        this.routT[i] = 5.5 + ((i * 5) % 9) * 0.45;
        this.wp[i] = 0;
        // Inside walls a flight 62 m straight back ends in masonry or outside
        // the curtain. Run for the furthest clear point on that line, and run
        // through the gates to get there.
        if (WALLS.n) {
          for (let t = 1; t > 0.05; t -= 0.1) {
            const qx = this.ax[i] + fx * 62 * t, qz = this.az[i] + fz * 62 * t;
            if (wallClear(qx, qz, 2.0)) { this.gx[i] = qx; this.gz[i] = qz; break; }
            this.gx[i] = this.ax[i]; this.gz[i] = this.az[i];
          }
          this._wallRoute(i);
        }
        this.since[i] = 0;
        continue;
      }

      if (foe >= 0) {
        if (ord !== ORDERS.ENGAGE) { this.prevOrder[i] = ord; this.order[i] = ORDERS.ENGAGE; }
        this.quietT[i] = 0;
        this.since[i] = 0;
        this.foeX[i] = e.x[foe]; this.foeZ[i] = e.z[foe]; this.foeT[i] = 0;
      } else if (ord === ORDERS.ENGAGE) {
        this.quietT[i] += 0.3;
        if (this.quietT[i] > 2.2) {                // the fight moved on; reform
          this.order[i] = ORDERS.HOLD;
          this.obj[i] = 0; this.objSq[i] = -1;
          this.since[i] = 99;                      // and go and find the next one
          this._reanchor(i, e);
        }
      } else if (ord === ORDERS.HOLD) {
        // heart comes back to a squad that is left alone
        this.morale[i] += (0.72 - this.morale[i]) * 0.05;
      }
    }
  }

  /**
   * Put the anchor back on the squad. Both rally paths used the leader, which
   * is one man and is often the one who chased furthest; the tracked centroid
   * is the whole body and is what "where the survivors actually are" meant.
   * The leader is the fallback for the tick before the first track pass.
   */
  _reanchor(i, e) {
    if (this.tracked[i] > 0) { this.ax[i] = this.cx[i]; this.az[i] = this.cz[i]; return; }
    const L = this.leader[i];
    if (L >= 0 && e.alive[L] && e.state[L] !== ST.DEAD) { this.ax[i] = e.x[L]; this.az[i] = e.z[L]; }
  }

  /**
   * Follow the nearest road towards (tx,tz) — this is what puts marching
   * columns ON the road instead of cutting across the fields.
   */
  _road(x, z, tx, tz) {
    let best = null, bd = 1e9;
    for (let k = 0; k < ROADS.length; k++) {
      ROADS[k].line.query(x, z, _r);
      if (_r.d < bd) { bd = _r.d; best = ROADS[k]; _r2.s = _r.s; }
    }
    if (!best || bd > 16) { _at.x = tx; _at.z = tz; return; }
    best.line.query(tx, tz, _r);
    const goalS = _r.s;
    if (_r.d > 26) { _at.x = tx; _at.z = tz; return; }   // goal is off-road
    const dir = goalS > _r2.s ? 1 : -1;
    const s = _r2.s + dir * 14;
    best.line.at(dir > 0 ? Math.min(s, goalS) : Math.max(s, goalS), _at);
  }
}
